/**
 * DesignedConfirmationPage — the page the TENANT AUTHORED, on the customer's
 * screen. `src/manage`, above the policy-shaped actions.
 *
 * APPOINTMENTS AND EVENTS, AFTER THE MANAGE EXCHANGE (2026-09-11; appointments
 * only from 2026-09-10). `ManagePage` waits for the session to identify the
 * journey type, then mounts this component for an appointment or an event
 * registration and hands it that type. Queue links keep their policy-shaped
 * Manage view until the public lane serves queues; invalid, expired or
 * unreachable Manage links do not start an independent public confirmation
 * read. Once mounted, this component makes the one public confirmation-page
 * call using the booking id in the path — the SAME operation for both kinds;
 * the server's events-owned door answers a registration with the same shape.
 *
 * THE SAME `CSCanvas` THE DESIGNER USES — that is the point of the shared
 * component, and the only difference is `live`, which suppresses the authoring
 * annotations and follows the server's download flags. If this rendered its own
 * copy of the sections, the designer would stop being a preview of anything.
 *
 * IT ALSO DOES THE ACTING, since the 2026-09-07 operator ruling: the customer
 * sees EXACTLY the authored page and nothing extra, so the platform's own hero,
 * detail cards and action card are no longer rendered beneath it. Move, Cancel,
 * the documents and the customer's details are the authored sections' controls
 * now. This component does not implement any of them — it takes a `CSWiring`
 * from `ManagePage`, which owns the session, the dialogs and the step-up. The
 * canvas knows how a control looks; the surface holding the session knows what
 * is permitted and what pressing it runs. That holds for an event exactly as
 * for an appointment: the session's own action flags decide what is offered,
 * and this file grants nothing a flag withheld.
 *
 * A SECTION LEFT OFF THE PAGE MEANS THE ACTION IS NOT OFFERED. That is the
 * ruling, not an oversight: the authored page is the whole surface.
 *
 * `onPhase` is how the platform fallback works. If this page cannot load at all
 * — which is NOT the 404, that renders as the designed expired state — the
 * customer would otherwise be left with nothing, so `ManagePage` puts its own
 * view back. That is a genuine failure path, not a second design.
 */
import { BookingPageSkeleton } from "./BookingPageSkeleton";
import { useEffect, useState } from "react";
/* React 19's types no longer declare a global JSX namespace, so a bare
   `JSX.Element` annotation is TS2503 under the kit's strict config. Imported the
   same way `ManagePage.tsx` imports it. */
import type { JSX } from "react";
import { getPublicBookingConfirmationPage } from "@v0y4ge/api-client";
import { t } from "@v0y4ge/i18n-runtime";
import type { PublicConfirmationPageResponse } from "@v0y4ge/api-contracts";
import { CSCanvas, type CSPageState, type CSWiring } from "../screens/journeys/confirmation-page";
import { csMigrate, type CSType } from "../screens/journeys/confirmation-sections";
import {
  isLinkInvalid, toActions, toCSBooking, toCSJourneyType, toCSPageFields, toCSPageState, toMasking,
} from "./confirmation-page-view";
import { readSegment } from "./manage-session";
import { takeEarlyPageRead } from "./manage-early";

/* The three sections an invalid link may render — the ones that carry no
   booking data. As ROWS, not ids: `csMigrate` keeps only entries with a string
   `type`, so a list of bare ids fell through to the full default state.
   Harmless only because `EXPIRED_ONLY` re-applies the same three inside the
   canvas, and a trap the moment the two lists differ (2026-09-09). */
const INVALID_SECTIONS = [
  { uid: "inv-brandHeader", type: "brandHeader" },
  { uid: "inv-banner", type: "banner" },
  { uid: "inv-footer", type: "footer" },
];

/* A live canvas can legitimately have no DOM children: every authored section
   may be hidden, inapplicable to this booking's journey type, or empty for this
   booking, and blank blocks are omitted too. The frame belongs to this customer
   surface, so hide it from the actual rendered shape rather than duplicating
   the kit's applicability and runtime-data rules here. `:has()` is already
   used elsewhere in the kit. */
const EMPTY_CANVAS_FRAME_CSS = `
  .mng-confirmation-canvas-frame:has(> .nj-sections:empty) {
    display: none;
  }
`;

type Phase =
  | { k: "loading" }
  | { k: "ready"; res: PublicConfirmationPageResponse }
  /** The 404. Not an error state: a link that does not identify a booking is a
   *  thing the page is designed to say, and `CSCanvas` renders it from the
   *  refusal with every section that carries booking data withheld. */
  | { k: "invalid" }
  /** Anything else. The page says nothing rather than guessing — the actions
   *  card below still works, so a failure here must not take the surface down. */
  | { k: "failed" };

export type DesignedPhase = Phase["k"];

export type ConfirmationPageContentProps = {
  res: PublicConfirmationPageResponse;
  /** The journey type the SURFACE asked for (2026-09-11) — the session's own
   *  `journey_type`, handed down by `ManagePage`. Defaults to `appointment` so
   *  the designer's draft preview and every earlier caller are unchanged. The
   *  loading and invalid states, which have no booking to read, render as this
   *  type; a loaded page renders as the type the RESPONSE says its booking is
   *  (`toCSJourneyType`), falling back to this one only when the wire is
   *  silent. */
  journeyType?: CSType;
  /** The authenticated view preserves reschedule history absent from public status. */
  rescheduled?: boolean;
  /** From `ManagePage`: what the controls may do. Absent until the session
   *  lands, which is deliberate — the page renders its first state with no
   *  session at all, off the public response's own flags. */
  wiring?: CSWiring;
  /** The customer's own details, from the SESSION view (2026-09-07).
   *
   *  They cannot come off the public lane: it carries a masked `customerName`
   *  and no email at all. The deleted `YouCard` was the only thing that ever
   *  read `view.customer`, so removing it left `yourDetails` able to render a
   *  name and never an email — while the designer previewed both, so an author
   *  placed the section having seen two lines and every real customer got one.
   *  `show_customer_data: false` nulls the contact members, so absent stays
   *  absent here rather than becoming a placeholder. */
  customer?: { name?: string | null; email?: string | null };
  /** The booking's video room, from the SESSION view's virtual location
   *  (2026-09-09). The public lane carries no join link, so without this the
   *  meeting section drew "Join the meeting" with nothing behind it — on
   *  in-person and phone bookings too. `virtual` is what decides the section is
   *  applicable at all; `joinUrl` is null until the tenant's reveal window. */
  meeting?: { virtual?: boolean; joinUrl?: string | null; passcode?: string | null; status?: string | null; note?: string | null; provider?: string | null };
  /** The customer's own answers, rendered to strings by `ManagePage` from the
   *  session view's questions. Also session-only: the public lane carries none,
   *  so the authored `answers` section could never render on the live surface
   *  while the platform's AnswersCard was hidden beneath it. */
  answers?: { label: string; value: string }[];
  /** Staff "View as customer" (CC 0280): the booking already exists, so the banner says what it is
   *  instead of the just-booked "You're all set · A confirmation is on its way". */
  staffPreview?: boolean;
  /** The manage page (CC 2026-10-17 item N): the customer is coming back to a booking that already
   *  exists, so the banner says what it is instead of the just-booked "You're all set". */
  returnVisit?: boolean;
  /** After a reschedule: the previous start, formatted in the booking's zone by ManagePage (CC 0282). */
  movedFromLabel?: string;
};

/* THE FIRST READ STARTS AT FIRST PAINT (CC 0315), alongside the session exchange. The
   server needs no session for it, so waiting for the exchange only added a second wait.
   Nothing from it is drawn before the exchange answers: this component is still mounted
   only on ManagePage's ready branch, so a refused link (dead state) or a queue link (its
   own view) never renders it, and the answer is dropped unread. The first mount takes the
   started promise; every later read (a new revision) is a fresh request. */
/* Started by manage-early.ts (CC 0337), which manage-main calls before the app loads. */
function takeEarlyRead(bookingId: string): Promise<unknown> | null { return takeEarlyPageRead(bookingId); }

export default function DesignedConfirmationPage(props: Omit<ConfirmationPageContentProps, "res"> & {
  onPhase?: (phase: DesignedPhase) => void;
  /** The surface's "Opening your booking…" string, so the one skeleton reads the same in both places. */
  loadingLabel?: string;
  /** Refresh after a successful booking mutation. */
  revision?: number;
} = {}): JSX.Element | null {
  const [phase, setPhase] = useState<Phase>({ k: "loading" });
  const [refreshing, setRefreshing] = useState(false);
  const bookingId = readSegment();
  const { onPhase, revision = 0 } = props;
  /* What the surface asked for. The states below that have no booking to read
     — loading, invalid — are drawn as this type; the loaded page reads its own. */
  const requestedType: CSType = props.journeyType ?? "appointment";

  useEffect(() => { onPhase?.(phase.k); }, [phase.k, onPhase]);

  useEffect(() => {
    if (!bookingId) { setPhase({ k: "failed" }); return; }
    let live = true;
    /* After a move, a cancel or an edit (a new revision) the page stays on screen while it is read
       again (CC 0304). Only the first read shows the skeleton. A failed re-read keeps the page the
       customer has. An invalid link still replaces it, because the link is no longer theirs. */
    setPhase((p) => (p.k === "ready" ? p : { k: "loading" }));
    if (revision > 0) setRefreshing(true);
    ((revision === 0 ? takeEarlyRead(bookingId) : null) ?? getPublicBookingConfirmationPage(bookingId))
      .then((res) => { if (live) setPhase({ k: "ready", res: res as PublicConfirmationPageResponse }); })
      .catch((e: unknown) => {
        if (!live) return;
        const invalid = isLinkInvalid(e);
        setPhase((p) => (p.k === "ready" && !invalid ? p : { k: invalid ? "invalid" : "failed" }));
        if (!invalid) console.warn("confirmation page re-read failed:", e);
      })
      .finally(() => { if (live) setRefreshing(false); });
    return () => { live = false; };
  }, [bookingId, revision]);

  if (phase.k === "failed") return null;

  if (phase.k === "loading") return <BookingPageSkeleton label={props.loadingLabel ?? "Opening your booking…"} />;

  /* An invalid link has no booking and no page document, so the canvas is given
     the three sections that carry neither. Everything else is withheld by the
     `expired` state itself. There is no response to read a type off, so the
     requested one stands. */
  if (phase.k === "invalid") {
    return (
      <div style={{ background: "#FFFFFF", border: "1px solid #E0E2E8", borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <CSCanvas
          live
          state={csMigrate(INVALID_SECTIONS, requestedType, "linked")}
          type={requestedType} surface="linked" pageState="expired"
          booking={{}} fields={{}} sel={null} onSelect={() => {}}
        />
      </div>
    );
  }

  return <div aria-busy={refreshing || undefined}><ConfirmationPageContent {...props} returnVisit res={phase.res} /></div>;
}

/** Shared customer rendering, also used by the designer draft preview. */
export function ConfirmationPageContent(props: ConfirmationPageContentProps): JSX.Element {
  const { res } = props;
  const page = res.data.page as Record<string, unknown> & typeof res.data.page;
  /* The booking's kind decides which sections apply — Guests on an event, the
     appointment summary rows on an appointment. The RESPONSE is the authority
     for a loaded page: the booking came from one funnel and the server says
     which. The requested type is only the fallback for a wire that is silent
     (2026-09-11). */
  const type = toCSJourneyType(res, props.journeyType ?? "appointment");
  /* The checklist is AUTHORED, not booked: it lives in the page document's open
     interior, so it comes off the page and not out of `toCSBooking`, which maps
     only what the booking itself carries. The section was rendering its empty
     strip because nothing joined the two halves.
     Filtered to non-empty strings — the interior is passthrough, so a malformed
     entry is possible, and a checklist line reading "[object Object]" is worse
     than one that is absent. */
  const prepare = Array.isArray(page.prepare)
    ? (page.prepare as unknown[]).filter((l): l is string => typeof l === "string" && l.trim() !== "")
    : undefined;

  /* Masking comes off the response's manifest, and the value on the wire is
     ALREADY masked when it is on — nothing here masks anything, because doing
     it twice is how the two disagree. `canReveal` needs a channel as well as
     the flag; the settings validator refuses masking with an empty channel
     list, so the pair travels together and masked-with-no-way-out is
     unreachable.
     The session's wiring wins when it arrives: it carries the real handlers and
     the server's policy. Before then this is the first state, rendered with no
     session, which is the whole reason the public lane exists. */
  const mask = toMasking(res);
  /* Masking IS known pre-session — it rides on the public response — so
     yourDetails renders immediately. Policy is not: `policyKnown` is false until
     ManagePage hands over a session-derived wiring, and the manage and downloads
     sections render nothing until then rather than guessing. */
  const wiring: CSWiring = {
    masked: mask.masked,
    canReveal: mask.masked && mask.channels.length > 0,
    ...(res.data.actions.walletPassUrl ? { walletPassUrl: res.data.actions.walletPassUrl } : {}),
    ...(props.wiring ?? {}),
    policyKnown: props.wiring?.policyKnown === true,
  };
  const booking = toCSBooking(res);
  /* TWO WINDOWS, ONE SCREEN (2026-09-09). `toCSPageState` derives `closed` from
     the booking worker's `cancellableUntil`, while the manage worker answers
     `actions.reschedule.allowed` / `actions.cancel.allowed` — so a booking the
     manage worker would still let the customer move could render as closed and
     withhold both controls. The manage worker is the authority on what it will
     permit: once its policy has arrived and allows either action, `closed` is
     not what happened. Before it arrives the derived state stands. */
  const bookingState = toCSPageState(res);
  // Reschedule history must not override a booking that has already ended.
  const derived = props.rescheduled && res.data.booking.status === "confirmed"
    && (bookingState === "confirmed" || bookingState === "closed")
    ? "rescheduled" : bookingState;
  const pageState: CSPageState = derived === "closed" && wiring.policyKnown && (wiring.canMove || wiring.canCancel)
    ? "confirmed"
    : derived;
  const withCustomer = {
    ...booking,
    ...(prepare && prepare.length ? { prepare } : {}),
    /* The customer's own details, for the yourDetails section. The public
       lane's `customerName` is the first paint — already masked when masking is
       on — and the session's own values replace it when they arrive, which is
       also the only source of the email. */
    ...(res.data.booking.customerName ? { customerName: res.data.booking.customerName } : {}),
    ...(props.customer?.name ? { customerName: props.customer.name } : {}),
    ...(props.customer?.email ? { customerEmail: props.customer.email } : {}),
    /* Session-only, all of them: the public lane carries none. */
    ...(props.meeting?.virtual ? { virtual: true } : {}),
    ...(props.meeting?.joinUrl ? { joinUrl: props.meeting.joinUrl } : {}),
    ...(props.meeting?.passcode ? { passcode: props.meeting.passcode } : {}),
  ...(props.meeting?.status ? { meetingStatus: props.meeting.status } : {}),
  ...(props.meeting?.note ? { meetingNote: props.meeting.note } : {}),
  ...(props.meeting?.provider ? { meetingProvider: props.meeting.provider } : {}),
    ...(props.answers && props.answers.length ? { answers: props.answers } : {}),
    ...(props.movedFromLabel ? { movedFromLabel: props.movedFromLabel } : {}),
  };

  /* An event registration's return visit says registration, not booking (CC 2026-10-19 walk 3 item 9). */
  const returnTitle = type === "event" ? t("journeys.confirmationPage.returnVisit.titleEvent", "Your registration") : t("journeys.confirmationPage.returnVisit.title", "Your booking");
  return (
    <>
      <style>{EMPTY_CANVAS_FRAME_CSS}</style>
      <div className="mng-confirmation-canvas-frame" style={{ background: "var(--mng-page, var(--jrni-color-surface-canvas, #FFFFFF))", border: "1px solid #E0E2E8", borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <CSCanvas
          live
          actions={toActions(res)}
          state={csMigrate(page.sections, type, "linked")}
          type={type}
          surface="linked"
          pageState={pageState}
          wiring={wiring}
          booking={withCustomer}
          fields={props.staffPreview && (pageState === "confirmed" || pageState === "closed")
            ? { ...toCSPageFields(res), bannerTitle: "Booking confirmed", bannerSub: "This is the customer's page for this booking." }
            : props.returnVisit && pageState === "confirmed"
            ? { ...toCSPageFields(res), bannerTitle: returnTitle, bannerSub: type === "event" ? t("journeys.confirmationPage.returnVisit.subEvent", "Your registration details are below.") : t("journeys.confirmationPage.returnVisit.sub", "Your booking details are below.") }
            : props.returnVisit && pageState === "closed"
            /* Closed keeps its own sub-line: it says why the booking can no longer be changed online. */
            ? { ...toCSPageFields(res), bannerTitle: returnTitle, bannerSub: undefined }
            : toCSPageFields(res)}
          content={Array.isArray(page.content) ? (page.content as ReadonlyArray<{ uid: string; type: string; text?: unknown }>) : undefined}
          accent="var(--jrni-color-primary-1, #5142C7)"
          sel={null}
          onSelect={() => {}}
        />
      </div>
    </>
  );
}
